import { Color3, Material, Mesh, Vector3 } from 'babylonjs';
import type { Vec3 } from '../../solver/types';
import { PlaneFinderFxPlugin } from './PlaneFinderFxPlugin';

export interface CullParams {
  origin: Vec3;
  normal: Vec3; // toward the camera
  front: number; // kept distance toward the camera
  back: number; // kept distance away from the camera
  fade: number; // 0 → outside fully visible, 1 → outside hard-culled (dither-dissolve between)
}

type Phase = 'idle' | 'ring' | 'reveal' | 'hold' | 'reject';

// Bright cyan reads clearly over the (often pale, baked-lit) model texture — a
// near-white highlight was too easy to lose against light surfaces.
const HIGHLIGHT = new Color3(0.25, 0.85, 1.0);
const REJECT = new Color3(1.0, 0.35, 0.3);

/**
 * Drives the pulse / reveal FX over a set of model meshes via one PlaneFinderFxPlugin per
 * material. The mesh order passed to `attach` must match the per-mesh wave-depth
 * arrays passed to `reveal` (i.e. the snapshot extraction order).
 *
 * Lifecycle: tap() (immediate radiating ring while the worker computes) →
 * reveal() (wavefront over the region by BFS depth) → settles to a held
 * highlight; reject() flashes red; clear()/captureMode() suppress the FX.
 */
export class RegionFx {
  private plugins: PlaneFinderFxPlugin[] = [];
  private meshes: Mesh[] = [];
  private diag = 1;
  private ringSpeed = 1;
  private ringPeriod = 1;
  private phase: Phase = 'idle';
  private t = 0;
  private maxDepth = 1;
  private revealDuration = 0.5;
  private origin = Vector3.Zero();
  private suspended = false;

  attach(meshes: Mesh[], diag: number): void {
    this.detach();
    this.diag = Math.max(diag, 1e-3);
    this.ringSpeed = 0.35 * this.diag;
    this.ringPeriod = this.diag / this.ringSpeed; // ≈ 2.85s for a full sweep
    const byMaterial = new Map<Material, PlaneFinderFxPlugin>();
    for (const mesh of meshes) {
      const mat = mesh.material;
      if (!mat) continue;
      let plugin = byMaterial.get(mat);
      if (!plugin) {
        plugin = new PlaneFinderFxPlugin(mat);
        byMaterial.set(mat, plugin);
        this.plugins.push(plugin);
      }
      this.meshes.push(mesh);
      mesh.setVerticesData('ejDepth', new Float32Array(mesh.getTotalVertices()).fill(-1), false, 1);
    }
    this.setAll((p) => {
      p.mode = 0;
      p.strength = 0;
    });
  }

  detach(): void {
    for (const p of this.plugins) {
      p.mode = 0;
      p.strength = 0;
      p.cullEnabled = 0;
    }
    this.plugins = [];
    this.meshes = [];
    this.phase = 'idle';
  }

  private setAll(fn: (p: PlaneFinderFxPlugin) => void): void {
    for (const p of this.plugins) fn(p);
  }

  /** Immediate radiating ring from a tap point (plays while analysis runs). */
  tap(worldOrigin: Vector3): void {
    this.origin.copyFrom(worldOrigin);
    this.phase = 'ring';
    this.t = 0;
    this.setAll((p) => {
      p.mode = 1;
      p.pulseOrigin.copyFrom(worldOrigin);
      p.pulseTint = HIGHLIGHT;
      p.pulseWidth = 0.04 * this.diag;
      p.pulseRadius = 0;
    });
  }

  /**
   * Reveal the detected region by BFS depth (per-mesh ejDepth attributes). With
   * `animate` (the default, on first tap) the wavefront sweeps over the region;
   * with `animate = false` (live re-detection while dragging the sensitivity
   * slider) it snaps straight to the solid hold so the highlight doesn't flicker.
   */
  reveal(maxDepth: number, depthsPerMesh: Float32Array[], animate = true): void {
    for (let i = 0; i < this.meshes.length; i++) {
      const arr = depthsPerMesh[i];
      if (arr) this.meshes[i].setVerticesData('ejDepth', arr, false, 1);
    }
    this.maxDepth = Math.max(1, maxDepth);
    this.revealDuration = Math.min(0.9, Math.max(0.25, this.maxDepth * 0.012));
    this.t = 0;
    this.setAll((p) => {
      p.tint = HIGHLIGHT;
      p.waveMaxDepth = this.maxDepth;
      p.pulseRadius = this.diag * 2; // push the ring offscreen; the wave takes over
      p.strength = 1;
    });
    if (animate) {
      this.phase = 'reveal';
      this.setAll((p) => {
        p.mode = 2;
        p.waveProgress = 0;
      });
    } else {
      this.phase = 'hold';
      this.setAll((p) => {
        p.mode = 3; // solid region highlight, no sweep
        p.waveProgress = 1;
      });
    }
  }

  /** Failed detection (tiny/curved pick): a quick red ring flash. */
  reject(worldOrigin: Vector3): void {
    this.origin.copyFrom(worldOrigin);
    this.phase = 'reject';
    this.t = 0;
    this.setAll((p) => {
      p.mode = 1;
      p.pulseOrigin.copyFrom(worldOrigin);
      p.pulseTint = REJECT;
      p.pulseWidth = 0.05 * this.diag;
      p.pulseRadius = 0;
      p.strength = 1;
    });
  }

  clear(): void {
    this.phase = 'idle';
    this.setAll((p) => {
      p.mode = 0;
      p.strength = 0;
    });
  }

  /** Drive the per-fragment cull (slab around the plane). Pass null to disable.
   *  Independent of the pulse/reveal mode, so it survives capture (FX suppressed). */
  setCull(params: CullParams | null): void {
    this.setAll((p) => {
      if (!params) {
        p.cullEnabled = 0;
        return;
      }
      p.cullEnabled = 1;
      p.cullOrigin.copyFromFloats(params.origin[0], params.origin[1], params.origin[2]);
      p.cullNormal.copyFromFloats(params.normal[0], params.normal[1], params.normal[2]);
      p.cullFront = params.front;
      p.cullBack = params.back;
      p.cullFade = params.fade;
    });
  }

  /** Suppress FX during capture; restores the held highlight afterward. */
  captureMode(on: boolean): void {
    this.suspended = on;
    if (on) {
      this.setAll((p) => {
        p.mode = 0;
        p.strength = 0;
      });
    } else if (this.phase === 'hold' || this.phase === 'reveal') {
      this.setAll((p) => {
        p.mode = 3;
        p.strength = 1;
      });
    }
  }

  /** Advance animations. `dt` is seconds since the last frame. */
  update(dt: number): void {
    if (this.suspended || this.phase === 'idle' || this.plugins.length === 0) return;
    this.t += dt;

    if (this.phase === 'ring') {
      const radius = this.ringSpeed * (this.t % this.ringPeriod);
      const strength = Math.min(1, this.t / 0.1);
      this.setAll((p) => {
        p.pulseRadius = radius;
        p.strength = strength;
      });
    } else if (this.phase === 'reveal') {
      const progress = Math.min(1, this.t / this.revealDuration);
      this.setAll((p) => {
        p.waveProgress = progress;
      });
      if (progress >= 1) {
        this.phase = 'hold';
        this.setAll((p) => {
          p.mode = 3;
        });
      }
    } else if (this.phase === 'reject') {
      const k = Math.max(0, 1 - this.t / 0.25);
      const radius = this.ringSpeed * this.t;
      this.setAll((p) => {
        p.pulseRadius = radius;
        p.strength = k;
      });
      if (k <= 0) this.clear();
    }
    // 'hold' is steady; nothing to advance.
  }
}
